import { useCallback, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE, type Paginated } from '#shared/types';
import { api } from '#client/lib/api';
import { useDebounced } from './useDebounced';
import { useKeyedRequest } from './useKeyedRequest';

/** A value that can be sent as a query parameter. */
type QueryValue = string | number | boolean | undefined;

interface UseResourceListOptions {
  /** API path, e.g. `/patients`. */
  path: string;
  limit?: number | undefined;
  /** Milliseconds to wait before issuing a search request. */
  debounceMs?: number | undefined;
  /**
   * Extra filters sent alongside search and pagination.
   *
   * Folded into the request key, so an inline object literal is fine here — its
   * identity changing every render does not re-fire the request, only its
   * contents do.
   */
  extraQuery?: Record<string, QueryValue> | undefined;
}

export interface UseResourceListResult<T> {
  items: T[];
  count: number;
  loading: boolean;
  error?: string | undefined;
  search: string;
  setSearch: (value: string) => void;
  page: number;
  setPage: (page: number) => void;
  limit: number;
  refresh: () => void;
}

/**
 * List + search + paginate, in one place for every list screen.
 *
 * Request cancellation — so a fast typist cannot have an earlier response
 * overwrite a later one — and the derived `loading` live in `useKeyedRequest`.
 */
export function useResourceList<T>({
  path,
  limit = DEFAULT_PAGE_SIZE,
  debounceMs = 300,
  extraQuery,
}: UseResourceListOptions): UseResourceListResult<T> {
  const [search, setSearchValue] = useState('');
  const [page, setPage] = useState(1);

  // Resetting to page 1 happens in setSearch, so the debounce only ever affects
  // which term is sent, never which page.
  const debouncedSearch = useDebounced(search, debounceMs);

  // Compared by value: `extraQuery` is written as an object literal at every
  // call site, so its identity changes on every render while its contents
  // rarely do. Keying on the serialised form is what lets a caller pass one
  // inline without re-firing the request each render.
  const extraQueryKey = JSON.stringify(extraQuery ?? {});

  const query = useMemo(
    () => ({ ...extraQuery, page, limit, search: debouncedSearch || undefined }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- extraQueryKey covers extraQuery
    [extraQueryKey, page, limit, debouncedSearch],
  );

  const request = useKeyedRequest<Paginated<T>>(
    JSON.stringify({ path, query }),
    (signal) => api.list<T>(path, query, signal),
    'Could not load records',
  );

  const setSearch = useCallback((value: string) => {
    setSearchValue(value);
    setPage(1);
  }, []);

  return useMemo(
    () => ({
      items: request.data?.items ?? [],
      count: request.data?.count ?? 0,
      loading: request.loading,
      error: request.error,
      search,
      setSearch,
      page,
      setPage,
      limit,
      refresh: request.refresh,
    }),
    [request, search, setSearch, page, limit],
  );
}
